import { FiltersType, ListingRequest, ListingResponse } from '@/api/types';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import { Group } from '@/client/groups';
import { Role } from '@/client/roles';
import { BulkUserActionFailureReason } from '../enums/bulk-user-action-failure-reason.enum';
import { UserOrigin } from './origins.enum';
import { User } from './user.type';

export type UsernameLookupResult = {
  id: string;
  active: boolean;
  deletedAt: string | null;
  firstName: string | null;
  lastName: string | null;
  gracePeriodDays: number | null;
  isAnonymized: boolean;
};

export type ListUsersRequest = ListingRequest;
export type ListRoleRequest = ListingRequest;
export type ListUsersResponse = ListingResponse<User>;
export type ListRoleResponse = ListingResponse<Role>;

export type UserFormState = {
  firstName?: string;
  lastName?: string;
  username?: string;
  name?: string;
  email?: string;
  password?: string;
  role?: Role;
  scopeGroups?: Group[];
  active?: boolean;
  branch?: Branch;
  groups?: Group[];
  language?: string;
  isManual?: boolean;
  assignedAccounts?: Account[];
  origin?: UserOrigin;
  expiresOn?: Date;
  reactivatesOn?: Date;
  isTemporary: boolean;
  freeTrialCheck?: boolean;
  manuallyDeactivated?: boolean;
  manuallyDeactivatedByUserName?: string;
  manuallyDeactivatedOn?: Date;
  domainVerified?: boolean;
};

export type SaveUserRequest = {
  accountId: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password?: string;
  active?: true;
  role?: string;
  customRole?: string | null;
  scopeGroups?: string[];
  branch?: string;
  groups?: string[];
  language?: string;
  origin?: UserOrigin;
  assignedAccounts?: string[];
  expiresOn?: Date;
  reactivatesOn?: Date;
};

export type UpdateUser = {
  email?: string;
  role?: string;
  customRole?: string | null;
  scopeGroups?: string[];
  active?: boolean;
  firstName?: string;
  lastName?: string;
  username?: string;
  password?: string;
  branch?: string;
  groups?: string[];
  companyName?: string;
  department?: string;
  manager?: string;
  managerEmail?: string;
  managerUserId?: string;
  country?: string;
  jobTitle?: string;
  mobilePhone?: string;
  officeLocation?: string;
  origin?: UserOrigin;
  assignedAccounts?: string[];
  expiresOn?: Date;
  reactivatesOn?: Date;
  isTemporaryPassword?: boolean;
  avatar?: string;
  hubspotMeetingLink?: string;
};

export type UpdateUserRequest = {
  userId: string;
  accountId: string;
  updates: UpdateUser;
};

export type DeleteUserRequest = {
  userId: string;
  accountId: string;
};

export type DeleteUsersBulkRequest = {
  userIds: string[];
  accountId: string;
};

export type RestoreUserRequest = {
  userId: string;
  accountId: string;
};

export type PermanentlyRemoveUserRequest = {
  userId: string;
  accountId: string;
};

export type RestoreUsersBulkRequest = {
  userIds: string[];
  accountId: string;
};

export type PermanentlyRemoveUsersBulkRequest = {
  userIds: string[];
  accountId: string;
};

export type BulkUserActionFailure = {
  userId: string;
  reason: BulkUserActionFailureReason;
};

export type BulkUserActionResult = {
  succeeded: string[];
  failed: BulkUserActionFailure[];
};

export type ExportAccountUsersRequest = {
  filters: FiltersType | undefined;
  exportKey: string;
  accountId: string;
  columns?: string[];
};

export type UsersExportFormValues = {
  isAllData: boolean;
  areAllColumns: boolean;
};

export enum UsersBulk {
  ADD = 'add',
  REMOVE = 'remove',
}

export enum EntityEnum {
  USER = 'user',
  BRANCH = 'branch',
  GROUP = 'group',
  ACCOUNT = 'account',
}
